import React from "react"
import styled from "styled-components"
import {FormattedNumber, useIntl} from "react-intl"

import Flex from "@irati/ui/src/Flex"
import FlexiPointsIcon from "@irati/ui/images/flexi_point.svg"
import RewardPointsIcon from "@irati/ui/images/reward_point.svg"
import {PaymentCurrencyType} from "@irati/types"

import {PointsBlend} from "@irati/ui/src/Logos"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {resolveRewardPointsName} from "../../rewardPoints/resolveRewardPointsName"
import {getNormalisedValue} from "../../currency/formatCurrency"

const Image = styled.img`
  margin-right: ${({theme}) => theme.spacing.half};
`

const SplitPriceWrapper = styled.span`
  display: inline-flex;
  align-items: baseline;
`

const SplitPriceLarge = styled.span`
  font-size: ${({theme}) => theme.spacing.three};
  font-weight: 500;
  line-height: 1;
`

const SplitPriceSmall = styled.span`
  font-size: ${({theme}) => theme.spacing.oneAndHalf};
  font-weight: 700;
  line-height: 1;
`

export interface FormattedCurrencyProps extends Omit<FormattedNumber.Props, "value" | "currency"> {
  isCents?: boolean
  value: string | number
  currency: string
  style?: "currency" | "percent" | "number"
  rewardPointsName?: string
  splitDecimal?: boolean
}

/** @author: Richard Scoones
 *  @desc: Disabling lint rule for "style" as react-intl (badly) use it for non style stuff
 */

const FormattedCurrency: React.FC<FormattedCurrencyProps> = ({
  isCents = true,
  value,
  currency,
  maximumFractionDigits,
  style = "currency",
  rewardPointsName,
  splitDecimal = false,
  ...props
}) => {
  const intl = useIntl()
  const auth = useAuth()
  const resolvedRewardPointsName = resolveRewardPointsName(rewardPointsName ?? auth.business?.rewardPointsName)
  const decimal = maximumFractionDigits ?? 2
  const normalisedValue = Number(getNormalisedValue(isCents, currency, value).toFixed(decimal))

  if (splitDecimal) {
    const parts = new Intl.NumberFormat(intl.locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: decimal
    }).formatToParts(normalisedValue)
    const largePart = parts
      .filter((p) => ["currency", "integer", "group", "literal"].includes(p.type))
      .map((p) => p.value)
      .join("")
    const smallPart = parts
      .filter((p) => ["decimal", "fraction"].includes(p.type))
      .map((p) => p.value)
      .join("")

    return (
      <SplitPriceWrapper>
        <SplitPriceLarge>{largePart}</SplitPriceLarge>
        {smallPart && <SplitPriceSmall>{smallPart}</SplitPriceSmall>}
      </SplitPriceWrapper>
    )
  }

  switch (currency) {
    case PaymentCurrencyType.Blend:
      return (
        <Flex alignItems="center" gap={0.5}>
          <PointsBlend size="md" />
          <FormattedNumber {...props} value={normalisedValue} />
        </Flex>
      )
    case PaymentCurrencyType.Tt:
      return (
        <Flex alignItems="center">
          <Image src={FlexiPointsIcon} alt="Flexi Points" />
          <FormattedNumber {...props} value={normalisedValue} />
        </Flex>
      )
    case PaymentCurrencyType.Ep:
    case PaymentCurrencyType.Rp:
      return (
        <Flex alignItems="center">
          <Image src={RewardPointsIcon} alt={resolvedRewardPointsName} />
          <FormattedNumber {...props} value={normalisedValue} />
        </Flex>
      )
    default:
      return (
        <FormattedNumber
          {...props}
          style={style}
          currency={currency}
          value={normalisedValue}
          maximumFractionDigits={maximumFractionDigits}
        />
      )
  }
}

/* eslint-enable react/style-prop-object */

export default FormattedCurrency
